import React, { useEffect, useMemo } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { LoginRequestDto } from "@api-types/user/LoginRequestDto";
import DesktopLayout from "@components-shared/desktop/layout/DesktopLayout";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { GlobalLoader } from "@components/global-loader/GlobalLoader";
import { HeaderType } from "@data-types/layout/headerTypeEnum";
import { ConfigType } from "@data-types/use-scroll-restoration/useScrollRestoration";
import MaintenanceNotice from "@features/maintenance/desktop/MaintenanceNotice";
import {
	LogonBankIdButton,
	LogonMitIdButton,
} from "@features/membership/shared";
import RefreshPagePopup from "@features/membership/shared/login/RefreshPagePopup";
import { useRootAppStore } from "@hooks";
import { useScrollRestoration } from "@hooks/useScrollRestoration";
import { THEME_SETTINGS } from "@theme-settings";
import { getCurrentCulture } from "@utils";
import { setTabTitle } from "@utils";
import { observer } from "mobx-react";

import {
	OnBoardingFooter,
	OnBoardingSlider,
} from "../registration-types/shared";
import { LoginForm, LoginStatusMessage } from "./";

export default observer(function LoginPage() {
	const {
		userAuthStore: { loginStore },
	} = useRootAppStore();

	const { t } = useTranslation("login");

	const location = useLocation();

	const params = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);
		return Object.fromEntries(searchParams);
	}, [location]);

	const methods = useForm<LoginRequestDto>({
		mode: "onChange",
		defaultValues: {
			password: "",
		},
	});

	useScrollRestoration({ type: ConfigType.Basic });

	useEffect(() => {
		setTabTitle(t("login:TITLE"));

		loginStore.onInitializeLogin();
		return () => {
			loginStore.resetLoginErrorState(true);
		};
	}, []);

	useEffect(() => {
		loginStore.handleSsoLogin(params, methods);
	}, [params]);

	return (
		<ErrorBoundaryWithPropagation componentName={"Desktop/LoginPage.tsx"}>
			<DesktopLayout
				headerType={HeaderType.EMPTY}
				mainClassName="at-login l--main l--main--grow"
			>
				{loginStore.isLoading ? <GlobalLoader /> : null}

				<RefreshPagePopup
					shouldShowPageRefreshPopup={
						loginStore.shouldShowPageRefreshPopup
					}
				/>
				<section className="l--content l--content--full">
					<div className="l--content__main u-padd--reset">
						<div className="container container--med container--grow">
							<div className="card--onboarding card--onboarding--grow card--shadowy">
								<OnBoardingSlider />
								<div className="card--onboarding__form card--onboarding__form--center">
									<div className="card--onboarding__header card--onboarding__header--vertical">
										<div className="card--onboarding__title card--onboarding__title--full">
											{t<string>("START_BETTING_NOW")}
										</div>
										{/* in case login error exists, don't display status message, login error is handled in login form */}
										<LoginStatusMessage
											shouldDisplayStatus={
												!loginStore.loginError
											}
										/>

										<MaintenanceNotice />
									</div>

									<FormProvider {...methods}>
										<LoginForm store={loginStore} />
									</FormProvider>

									<LogonMitIdButton btnSize="base" />
									<LogonBankIdButton btnSize="base" />

									<div className="card--onboarding__footer u-type--sml u-color--text--secondary">
										<SignUpButton />
										<AccountLockSettingsUpdateButton />
									</div>
								</div>
							</div>

							<OnBoardingFooter />
						</div>
					</div>
				</section>
			</DesktopLayout>
		</ErrorBoundaryWithPropagation>
	);
});

function SignUpButton() {
	const { t } = useTranslation("login");

	if (!WEBPACK_IS_REGISTRATION_ENABLED) {
		return null;
	}

	return (
		<div>
			{t("NONEXISTENT_ACCOUNT")}
			<Link
				to={`/${getCurrentCulture()}/auth/register`}
				className="u-anchor u-anchor--primary u-mar--left--xtny"
			>
				{t("SIGN_UP_LABEL")}
			</Link>
		</div>
	);
}

function AccountLockSettingsUpdateButton() {
	const { t } = useTranslation();

	if (!THEME_SETTINGS.enableAccountLockUpdateOnLogin) {
		return null;
	}

	return (
		<div>
			{t("MEMBERSHIP.ACCOUNT_LOCK_UPDATE.LOGIN_LINK_MESSAGE")}
			<Link
				to={`/${getCurrentCulture()}/auth/account-lock-update`}
				className="u-anchor u-anchor--primary u-mar--left--xtny"
			>
				{t("MEMBERSHIP.ACCOUNT_LOCK_UPDATE.LOGIN_LINK_LABEL")}
			</Link>
		</div>
	);
}
